import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { useDispatch, useSelector } from "react-redux";
import { setData } from "../../../../store/tree-view/tree-view.slice";
import InputTextField from "../../../../components/input/inputTextField";
import {
  ButtonBack,
  ButtonPrimary,
} from "../../../../components/button/ButtonCustoms";
import ModalCustom from "../../../../components/modal/ModalCustom";
import { ReactComponent as Iconadd } from "../../../../assets/icons/add.svg";
import { ReactComponent as Iconpassword } from "../../../../assets/icons/password-check2.svg";
import { ReactComponent as Iconbag } from "../../../../assets/icons/bag.svg";

import { colors_button } from "../../../../components/button/util.button";
import { alertActive, alertSuccess } from "../../../../components/alert/swal";
import { useTranslation } from "react-i18next";
import ContainerLayout from "../../../../components/container-layout/ContainerLayout";
import NavigateListCustom from "../../../../components/navigation/NavigationListCustom";
import { routeName } from "../../../../routes/routeName";
import { edpexStandardPointMasterAPI } from "../../../../api/edpex-standard-point-master/edpex-standard-point-master.api";
import { edpexStandardPointDetailMasterAPI } from "../../../../api/edpex-standard-point-detail-master/edpex-standard-point-detail-master.api";

// Types
interface EdpexStandardPoint {
  id_edpex_standard_point: number;
  seq: number;
  edpex_standard_point_name: string;
  status: string;
  created_id: number;
  created_date: string;
  modified_id: number;
  modified_date: string;
}

interface EdpexStandardPointDetail {
  id_edpex_standard_point_detail: number;
  id_edpex_standard_point: number | null;
  seq: number | null;
  edpex_standard_point_detail_name: string | null;
  status: string;
  created_id: number | null;
  created_date: string;
  modified_id: number | null;
  modified_date: string;
}

type Props = {};

const RecursiveTreeView = ({ nodes, index }: any) => {
  const dispatch = useDispatch();
  return (
    <TreeItem
      sx={{
        color: "#828282",
        ".Mui-selected": {
          color: "#FCAF17 !important",
          backgroundColor: "#fff8e1 !important",
          "& .MuiTreeItem-label": {
            color: "#FCAF17 !important",
            "& .MuiTypography-root": {
              color: "#FCAF17 !important",
            },
          },
        },
        "&:hover": {
          backgroundColor: "transparent !important",
        },
        "& .MuiTreeItem-content": {
          padding: "0px 8px",
        },
      }}
      onClick={() => dispatch(setData(nodes))}
      key={nodes.id_edpex_standard_point}
      itemId={nodes.id_edpex_standard_point.toString()}
      label={
        <Box
          key={nodes.id_edpex_standard_point}
          className="flex items-center px-4 py-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition-colors"
          sx={{
            "&:hover": {
              backgroundColor: "transparent !important",
            },
            "&:hover .MuiTypography-root": {
              color: "#374151",
            },
          }}
        >
          {/* Index Column - matches header width */}
          <div className="w-12 text-center">
            <Typography variant="body2">{nodes.seq || index}</Typography>
          </div>

          {/* Name Column - matches header flex-1 */}
          <div className="flex-1">
            <Typography>{nodes?.edpex_standard_point_name || ""}</Typography>
          </div>
        </Box>
      }
    />
  );
};

const TreeViewComponent = ({ treeData }: any) => {
  return (
    <div>
      {/* Header */}
      <div className="flex items-center py-2 px-4 font-medium">
        <div className="w-16 text-center">
          <Typography variant="subtitle2" className="font-semibold">
            ลำดับ
          </Typography>
        </div>
        <div className="flex-1">
          <Typography variant="subtitle2" className="font-semibold">
            ชื่อ
          </Typography>
        </div>
      </div>

      {/* Tree Content */}
      <SimpleTreeView>
        {treeData.map((node: EdpexStandardPoint, index: number) => (
          <RecursiveTreeView
            key={node.id_edpex_standard_point}
            nodes={node}
            index={index + 1}
          />
        ))}
      </SimpleTreeView>
    </div>
  );
};

const defaultFormStandardPoint = {
  id_edpex_standard_point: 0,
  seq: 0,
  edpex_standard_point_name: "",
  status: "active",
};

const defaultFormStandardPointDetail = {
  id_edpex_standard_point_detail: 0,
  id_edpex_standard_point: null,
  seq: null,
  edpex_standard_point_detail_name: "",
  status: "active",
};

const EdpexCriteria = (props: Props) => {
  const { profile } = useSelector((state: any) => state.profile);
  const { t, i18n } = useTranslation();

  const treeViewData = useSelector((state: any) => state?.treeViewData.data);
  const [formStandardPoint, setFormStandardPoint] = useState<any>(
    defaultFormStandardPoint
  );
  const [formStandardPointDetail, setFormStandardPointDetail] = useState<any>(
    defaultFormStandardPointDetail
  );
  const [openStandardPoint, setOpenStandardPoint] = useState<boolean>(false);
  const [openStandardPointDetail, setOpenStandardPointDetail] =
    useState<boolean>(false);
  const [isEditStandardPointDetail, setIsEditStandardPointDetail] =
    useState<boolean>(false);
  const [isEditableStandardPoint, setIsEditableStandardPoint] =
    useState<boolean>(false);

  const [standardPoints, setStandardPoints] = useState<EdpexStandardPoint[]>(
    []
  );
  const [standardPointDetails, setStandardPointDetails] = useState<
    EdpexStandardPointDetail[]
  >([]);

  const getStandardPoints = async () => {
    try {
      const res =
        await edpexStandardPointMasterAPI.getEdpexStandardPointMasterList();
      const data = res.data;
      if (data.status < 300) {
        setStandardPoints(data.data);
      } else {
        setStandardPoints([]);
      }
    } catch (error) {
      console.log(error);
      setStandardPoints([]);
    }
  };

  const getStandardPointDetails = async () => {
    try {
      const res =
        await edpexStandardPointDetailMasterAPI.getEdpexStandardPointDetailMasterList();
      const data = res.data;
      if (data.status < 300) {
        // Filter details based on selected standard point
        const filteredDetails = data.data.filter(
          (detail: EdpexStandardPointDetail) =>
            detail.id_edpex_standard_point ===
            treeViewData?.id_edpex_standard_point
        );
        setStandardPointDetails(filteredDetails);
      } else {
        setStandardPointDetails([]);
      }
    } catch (error) {
      console.log(error);
      setStandardPointDetails([]);
    }
  };

  useEffect(() => {
    getStandardPoints();
  }, []);

  useEffect(() => {
    if (treeViewData?.id_edpex_standard_point) {
      getStandardPointDetails();
    }
  }, [treeViewData]);

  const handleCloseStandardPoint = async () => {
    setOpenStandardPoint(false);
    setFormStandardPoint(defaultFormStandardPoint);
    setIsEditableStandardPoint(false);
  };

  const handleCreateStandardPoint = async () => {
    const body = {
      id_edpex_standard_point:
        formStandardPoint?.id_edpex_standard_point || undefined,
      seq: formStandardPoint.seq || 1,
      edpex_standard_point_name:
        formStandardPoint.edpex_standard_point_name || "",
      status: "active",
    };

    try {
      if (formStandardPoint.id_edpex_standard_point) {
        // Update existing
        const res = await edpexStandardPointMasterAPI.updateEdpexStandardPointMaster(
          formStandardPoint.id_edpex_standard_point,
          body,
        );
        if (res.status < 300) {
          alertSuccess(
            "แก้ไขเกณฑ์คะแนน Edpex",
            "แก้ไขเกณฑ์คะแนน Edpex เรียบร้อยแล้ว",
            undefined,
            () => {
              getStandardPoints();
              handleCloseStandardPoint();
            }
          );
        }
      } else {
        // Create new
        const res = await edpexStandardPointMasterAPI.createEdpexStandardPointMaster(body);
        if (res.status < 300) {
          alertSuccess(
            "เพิ่มเกณฑ์คะแนน Edpex",
            "เพิ่มเกณฑ์คะแนน Edpex เรียบร้อยแล้ว",
            undefined,
            () => {
              getStandardPoints();
              handleCloseStandardPoint();
            }
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteStandardPoint = async () => {
    alertActive(
      "ลบเกณฑ์คะแนน Edpex",
      "ท่านต้องการลบเกณฑ์คะแนน Edpex ใช่หรือไม่",
      undefined,
      undefined,
      async () => {
        try {
          const res = await edpexStandardPointMasterAPI.deleteEdpexStandardPointMaster(
            treeViewData.id_edpex_standard_point
          );
          if (res.status < 300) {
            alertSuccess(
              "ลบเกณฑ์คะแนน Edpex",
              "ลบเกณฑ์คะแนน Edpex เรียบร้อยแล้ว",
              undefined,
              () => {
                getStandardPoints();
              }
            );
          }
        } catch (error) {
          console.log(error);
        }
      }
    );
  };

  const handleCloseStandardPointDetail = async () => {
    setOpenStandardPointDetail(false);
    setFormStandardPointDetail(defaultFormStandardPointDetail);
    setIsEditStandardPointDetail(false);
  };

  const handleCreateStandardPointDetail = async () => {
    const body = {
      id_edpex_standard_point_detail:
        formStandardPointDetail?.id_edpex_standard_point_detail || undefined,
      id_edpex_standard_point: treeViewData?.id_edpex_standard_point,
      seq: formStandardPointDetail.seq || null,
      edpex_standard_point_detail_name:
        formStandardPointDetail.edpex_standard_point_detail_name || "",
      status: "active",
    };

    try {
      if (formStandardPointDetail.id_edpex_standard_point_detail) {
        // Update existing
        const res = await edpexStandardPointDetailMasterAPI.updateEdpexStandardPointDetailMaster(
          formStandardPointDetail.id_edpex_standard_point_detail,
          body,
        );
        if (res.status < 300) {
          alertSuccess(
            "แก้ไขเกณฑ์คะแนน ภายใต้",
            "แก้ไขเกณฑ์คะแนน ภายใต้ เรียบร้อยแล้ว",
            undefined,
            () => {
              getStandardPointDetails();
              handleCloseStandardPointDetail();
            }
          );
        }
      } else {
        // Create new
        const res = await edpexStandardPointDetailMasterAPI.createEdpexStandardPointDetailMaster(body);
        if (res.status < 300) {
          alertSuccess(
            "เพิ่มเกณฑ์คะแนน ภายใต้",
            "เพิ่มเกณฑ์คะแนน ภายใต้ เรียบร้อยแล้ว",
            undefined,
            () => {
              getStandardPointDetails();
              handleCloseStandardPointDetail();
            }
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteStandardPointDetail = async () => {
    alertActive(
      "ลบเกณฑ์คะแนน ภายใต้",
      "ท่านต้องการลบเกณฑ์คะแนน ภายใต้ ใช่หรือไม่",
      undefined,
      undefined,
      async () => {
        try {
          const res = await edpexStandardPointDetailMasterAPI.deleteEdpexStandardPointDetailMaster(
            formStandardPointDetail.id_edpex_standard_point_detail
          );
          if (res.status < 300) {
            alertSuccess(
              "ลบเกณฑ์คะแนน ภายใต้",
              "ลบเกณฑ์คะแนน ภายใต้ เรียบร้อยแล้ว",
              undefined,
              () => {
                getStandardPointDetails();
                setFormStandardPointDetail(defaultFormStandardPointDetail);
              }
            );
          }
        } catch (error) {
          console.log(error);
        }
      }
    );
  };

  return (
    <div>
      <ContainerLayout
        title={t("เกณฑ์คะแนน Edpex")}
        hidden_sub_title
        navigateContent={
          <NavigateListCustom
            navigateData={[
              {
                key: "1",
                name: t("ระบบ Edpex"),
                disabled_match: true,
                pathName: routeName.cms.edpexResultDisplay,
              },
              {
                key: "2",
                name: t("เกณฑ์คะแนน Edpex"),
                pathName: routeName.cms.edpexCriteria,
              },
            ]}
          />
        }
        content={
          <>
            <div className="w-full grid grid-cols-12 gap-4">
              {/* Left Panel - Standard Points */}
              <div className="col-span-12 md:col-span-12 lg:col-span-6 border-2 rounded-lg border-gray-300 relative h-[580px]">
                <div className="p-4">
                  <Typography variant="h5">{t("เกณฑ์ Edpex")}</Typography>
                  <div className="p-4 h-[390px] overflow-auto">
                    <TreeViewComponent treeData={standardPoints} />
                  </div>
                </div>

                <div className="flex flex-col md:flex-row justify-center gap-4 mt-[40px] bg-[#DDE1EB] p-[46px] absolute bottom-0 left-0 right-0">
                  <Button
                    sx={{
                      background: colors_button.success,
                      ":hover": {
                        background: colors_button.success,
                      },
                      color: "#fff",
                      borderRadius: "6px",
                      padding: "6px 12px",
                    }}
                    onClick={() => {
                      setOpenStandardPoint(true);
                    }}
                  >
                    <Iconadd className="me-1" />
                    {t("เพิ่ม")}
                  </Button>

                  {treeViewData?.id_edpex_standard_point && (
                    <>
                      <Button
                        sx={{
                          background: colors_button.primary,
                          ":hover": {
                            background: colors_button.primary,
                          },
                          color: "#fff",
                          borderRadius: "6px",
                          padding: "6px 12px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => {
                          setIsEditableStandardPoint(true);
                          setFormStandardPoint(treeViewData);
                          setOpenStandardPoint(true);
                        }}
                      >
                        <Iconpassword className="me-1" />
                        {t("แก้ไข")}
                      </Button>
                      <Button
                        sx={{
                          background: colors_button.red,
                          ":hover": {
                            background: colors_button.red,
                          },
                          color: "#fff",
                          borderRadius: "6px",
                          padding: "6px 12px",
                        }}
                        onClick={() => {
                          handleDeleteStandardPoint();
                        }}
                      >
                        <Iconbag className="me-1" />
                        {t("ลบ")}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Right Panel - Standard Point Details */}
              <div className="col-span-12 md:col-span-12 lg:col-span-6 border-2 rounded-lg border-gray-300 relative h-[580px]">
                <div className="p-4">
                  <Typography variant="h5">{t("เกณฑ์คะแนน ภายใต้")}</Typography>
                  {/* Header */}
                  <div className="flex items-center py-2 px-4 font-medium mt-4 rounded-t-lg">
                    <div className="w-16 text-center">
                      <Typography variant="subtitle2" className="font-semibold">
                        ลำดับ
                      </Typography>
                    </div>
                    <div className="flex-1">
                      <Typography variant="subtitle2" className="font-semibold">
                        ชื่อ
                      </Typography>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="h-[390px] overflow-auto rounded-b-lg">
                    {standardPointDetails?.map(
                      (item: EdpexStandardPointDetail, index: number) => {
                        const isSelected =
                          formStandardPointDetail?.id_edpex_standard_point_detail ===
                          item?.id_edpex_standard_point_detail;

                        return (
                          <div
                            key={item.id_edpex_standard_point_detail}
                            className="flex items-center px-4 py-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition-colors"
                            onClick={() => {
                              setFormStandardPointDetail(item);
                            }}
                            style={{
                              backgroundColor: isSelected
                                ? "#fff8e1"
                                : "transparent",
                            }}
                          >
                            {/* Index Column - matches header width */}
                            <div className="w-16 text-center">
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isSelected ? "#FCAF17" : "#4A4A4A",
                                  fontWeight: isSelected ? 600 : 400,
                                }}
                              >
                                {item.seq || index + 1}
                              </Typography>
                            </div>

                            {/* Name Column - matches header flex-1 */}
                            <div className="flex-1">
                              <Typography
                                sx={{
                                  color: isSelected ? "#FCAF17" : "#4A4A4A",
                                  fontWeight: isSelected ? 600 : 400,
                                }}
                              >
                                {item?.edpex_standard_point_detail_name || ""}
                              </Typography>
                            </div>
                          </div>
                        );
                      }
                    )}

                    {/* Empty State */}
                    {(!standardPointDetails ||
                      standardPointDetails.length === 0) && (
                      <div className="flex items-center justify-center h-32">
                        <Typography variant="body2" color="textSecondary">
                          {treeViewData?.id_edpex_standard_point
                            ? "ไม่มีข้อมูลเกณฑ์คะแนน ภายใต้"
                            : "กรุณาเลือกเกณฑ์ Edpex ก่อน"}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col md:flex-row justify-center gap-4 mt-[40px] bg-[#DDE1EB] p-[46px] absolute bottom-0 left-0 right-0">
                  <Button
                    sx={{
                      background: colors_button.success,
                      ":hover": {
                        background: colors_button.success,
                      },
                      color: "#fff",
                      borderRadius: "6px",
                      padding: "6px 12px",
                    }}
                    onClick={() => {
                      if (!treeViewData?.id_edpex_standard_point) {
                        alert("กรุณาเลือกเกณฑ์ Edpex ก่อน");
                        return;
                      }
                      setOpenStandardPointDetail(true);
                      setFormStandardPointDetail(
                        defaultFormStandardPointDetail
                      );
                    }}
                    disabled={!treeViewData?.id_edpex_standard_point}
                  >
                    <Iconadd className="me-1" />
                    {t("เพิ่ม")}
                  </Button>

                  {formStandardPointDetail?.id_edpex_standard_point_detail && (
                    <>
                      <Button
                        sx={{
                          background: colors_button.primary,
                          ":hover": {
                            background: colors_button.primary,
                          },
                          color: "#fff",
                          borderRadius: "6px",
                          padding: "6px 12px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => {
                          setOpenStandardPointDetail(true);
                          setIsEditStandardPointDetail(true);
                        }}
                      >
                        <Iconpassword className="me-1" />
                        {t("แก้ไข")}
                      </Button>

                      <Button
                        sx={{
                          background: colors_button.red,
                          ":hover": {
                            background: colors_button.red,
                          },
                          color: "#fff",
                          borderRadius: "6px",
                          padding: "6px 12px",
                        }}
                        onClick={() => {
                          handleDeleteStandardPointDetail();
                        }}
                      >
                        <Iconbag className="me-1" />
                        {t("ลบ")}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Standard Point Modal */}
            <ModalCustom
              title={
                isEditableStandardPoint
                  ? t("แก้ไขเกณฑ์คะแนน Edpex")
                  : t("เพิ่มเกณฑ์คะแนน Edpex")
              }
              subTitle={t("")}
              open={openStandardPoint}
              component={
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Order/Sequence Column */}
                    <div className="col-span-1">
                      <InputTextField
                        heading={t("ลำดับ")}
                        placeholder={t("ลำดับ")}
                        value={formStandardPoint?.seq || ""}
                        onchange={(e) =>
                          setFormStandardPoint({
                            ...formStandardPoint,
                            seq: parseInt(e.target.value) || 0,
                          })
                        }
                        disabled={isEditableStandardPoint}
                      />
                    </div>

                    {/* Name Column - Takes more space */}
                    <div className="col-span-1 md:col-span-1 lg:col-span-3">
                      <InputTextField
                        heading={t("ชื่อ")}
                        placeholder={t("ชื่อ")}
                        value={
                          formStandardPoint.edpex_standard_point_name || ""
                        }
                        onchange={(e) =>
                          setFormStandardPoint({
                            ...formStandardPoint,
                            edpex_standard_point_name: e.target.value,
                          })
                        }
                        disabled={isEditableStandardPoint}
                      />
                    </div>
                  </div>

                  <div className="flex justify-center gap-4">
                    <ButtonBack
                      onClick={() => {
                        handleCloseStandardPoint();
                      }}
                      buttonName={t("ยกเลิก")}
                    />
                    {!isEditableStandardPoint ? (
                      <ButtonPrimary
                        onClick={() => handleCreateStandardPoint()}
                        mode="add"
                        buttonName={t("ยืนยัน")}
                      />
                    ) : (
                      <ButtonPrimary
                        onClick={() => setIsEditableStandardPoint(false)}
                        buttonName={t("แก้ไข")}
                      />
                    )}
                  </div>
                </div>
              }
            />

            {/* Standard Point Detail Modal */}
            <ModalCustom
              title={
                isEditStandardPointDetail
                  ? t("แก้ไขเกณฑ์คะแนน ภายใต้")
                  : t("เพิ่มเกณฑ์คะแนน ภายใต้")
              }
              subTitle={t("")}
              open={openStandardPointDetail}
              component={
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="col-span-1">
                      <InputTextField
                        heading={t("ลำดับ")}
                        placeholder={t("ลำดับ")}
                        value={formStandardPointDetail?.seq || ""}
                        onchange={(e) =>
                          setFormStandardPointDetail({
                            ...formStandardPointDetail,
                            seq: parseInt(e.target.value) || null,
                          })
                        }
                      />
                    </div>

                    {/* Name Column - Takes more space */}
                    <div className="col-span-1 md:col-span-1 lg:col-span-3">
                      <InputTextField
                        heading={t("ชื่อ")}
                        placeholder={t("ชื่อ")}
                        value={
                          formStandardPointDetail?.edpex_standard_point_detail_name ||
                          ""
                        }
                        onchange={(e) =>
                          setFormStandardPointDetail({
                            ...formStandardPointDetail,
                            edpex_standard_point_detail_name: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <InputTextField
                      heading={t("ภายใต้เกณฑ์ Edpex")}
                      placeholder={t("ภายใต้เกณฑ์ Edpex")}
                      value={treeViewData?.edpex_standard_point_name || ""}
                      onchange={(e) => {}}
                      disabled
                    />
                  </div>

                  <div className="flex justify-center gap-4">
                    <ButtonBack
                      onClick={() => {
                        handleCloseStandardPointDetail();
                      }}
                      buttonName={t("ยกเลิก")}
                    />
                    {!isEditStandardPointDetail ? (
                      <ButtonPrimary
                        onClick={() => handleCreateStandardPointDetail()}
                        mode="add"
                        buttonName={t("ยืนยัน")}
                      />
                    ) : (
                      <ButtonPrimary
                        onClick={() => setIsEditStandardPointDetail(false)}
                        buttonName={t("แก้ไข")}
                      />
                    )}
                  </div>
                </div>
              }
            />
          </>
        }
      />
    </div>
  );
};

export default EdpexCriteria;
